import timeSync from '@/infrastructure/utils/timeSync';
import { DATE_CONSTANTS } from '@/shared/constants/dateConstants';
import { DateTime } from 'luxon';

const TIME_FORMAT = DATE_CONSTANTS.TIME_FORMAT;

/**
 * formatAMPM() method to perform the am or pm format with time.
 *
 * @param {date} date
 */
function formatAMPM(date) {
   const dt = DateTime.fromJSDate(date); // always interprets in local browser timezone
   const result = dt.toFormat('h:mm a').toLowerCase(); // "4:42 pm"
   return result;
}

/**
 * formatChatDate() method to perform convert into Today, Yesterday and Date format.
 *
 * @param {date} date
 */
export const formatChatDate = date => {
   const dateCurrent = timeSync.getSyncedNow().startOf('day'); // ← synced
   const dateMessage = DateTime.fromJSDate(date).startOf('day');
   const diff = dateCurrent.diff(dateMessage, 'days').days;

   if (diff === 0) return 'Today';
   if (diff === 1) return 'Yesterday';
   if (diff > 1 && diff <= 6) return DateTime.fromJSDate(date).toFormat('cccc');
   return DateTime.fromJSDate(date).toFormat('dd/MM/yy');
};

/**
 * formatChatTime() method to perform convert time into now, min ago, mins ago and date format.
 *
 * @param {date} date
 * @param {string} type
 */
export const formatChatTime = (date, type) => {
   if (type === 'message-card') {
      return formatAMPM(date);
   }

   const dateCurrent = timeSync.getSyncedNow();
   const dateMessage = DateTime.fromJSDate(date);
   const diffMinutes = dateCurrent.diff(dateMessage, 'minutes').minutes;
   const diffRounded = Math.floor(diffMinutes);

   if (diffRounded <= 0) return 'Now';
   if (diffRounded === 1) return `${diffRounded} min ago`;
   if (diffRounded < 60) return `${diffRounded} mins ago`;
   return dateMessage.toFormat(TIME_FORMAT);
};
/**
 * formatChatDateTime() method to perform the format date & time.
 *
 * @param {Date} date
 * @param {string} type
 */
export const formatChatDateTime = (date, type) => {
   const timeFormatted = formatChatTime(date, type);
   const dayFormatted = formatChatDate(date);

   if (type === 'recent-chat') {
      if (dayFormatted === 'Today') {
         return timeFormatted;
      }
      return dayFormatted;
   } else {
      if (typeof timeFormatted === 'string' && timeFormatted.match(/Now|mins? ago/i)) {
         return timeFormatted;
      }
      return dayFormatted;
   }
};

/**
 * formatRecentChatTime() — formats a date for the recent chat list:
 *   Today    → "4:42 pm"
 *   Yesterday → "Yesterday"
 *   Within 6 days → weekday name (e.g. "Monday")
 *   Older    → "dd/MM/yy"
 *
 * @param {Date} date
 */
export const formatRecentChatTime = date => {
   const dateCurrent = timeSync.getSyncedNow().startOf('day');
   const dateMessage = DateTime.fromJSDate(date).startOf('day');
   const diff = dateCurrent.diff(dateMessage, 'days').days;

   if (diff === 0) return formatAMPM(date);
   if (diff === 1) return 'Yesterday';
   if (diff > 1 && diff <= 6) return DateTime.fromJSDate(date).toFormat('cccc');
   return DateTime.fromJSDate(date).toFormat('dd/MM/yy');
};

/**
 * convertUTCTOLocalTimeStamp() method to perform convert UTC to local time format.
 *
 * @param {string} date - UTC datetime string e.g. "2026-03-19 11:12:24"
 */
export const convertUTCTOLocalTimeStamp = date => {
   // Treat the input as UTC (append Z), then Luxon converts it to local timezone automatically
   const normalised = date.replace(/-/g, '-').trim();
   const dt = DateTime.fromSQL(normalised, { zone: 'utc' }).isValid
      ? DateTime.fromSQL(normalised, { zone: 'utc' }).toLocal()
      : DateTime.fromISO(normalised + 'Z').toLocal();
   const result = dt.toJSDate();

   return result;
};

export const getLastSeenFormatter = secs => {
   if (secs === 0) return 'Online';

   const now = DateTime.now();
   const seen = now.minus({ seconds: secs });

   const HHMM = { hour: 'numeric', minute: 'numeric' };
   const timeStr = seen.toLocal().toJSDate().toLocaleTimeString('en-US', HHMM);

   const diffDays = now.startOf('day').diff(seen.startOf('day'), 'days').days;
   const weekday = seen.toFormat('cccc'); // e.g. "Monday"
   const dateStr = seen.toFormat('dd-MMM-yyyy');

   if (diffDays === 0) {
      return `last seen today at ${timeStr}`;
   } else if (diffDays === 1) {
      return `last seen yesterday at ${timeStr}`;
   } else if (diffDays <= 6) {
      return `last seen on ${weekday} at ${timeStr}`;
   } else {
      return `last seen ${dateStr}`;
   }
};

/**
 * Normalizes a timestamp to milliseconds. Handles 13-digit (ms) and 16-digit (micro-s) timestamps.
 *
 * @param {number|string} timestamp
 * @returns {number} Normalized timestamp in milliseconds
 */
export const normalizeTimestamp = timestamp => {
   if (!timestamp) return 0;
   const ts = Number(timestamp);
   return ts > 9999999999999 ? Math.floor(ts / 1000) : ts;
};

export const formatToCreatedAt = timestamp => {
   return DateTime.fromMillis(normalizeTimestamp(timestamp)).toFormat('yyyy-MM-dd HH:mm:ss.SSS');
};

/**
 * Formats an epoch timestamp into a displayable string (e.g., "Today at 10:00 AM").
 */
export const formatEpochDisplayTime = epochTimestamp => {
   if (!epochTimestamp) return null;
   const dateObj = new Date(normalizeTimestamp(epochTimestamp));
   const day = formatChatDate(dateObj) || 'Today';
   const time = formatChatTime(dateObj, 'message-card');
   return time ? `${day} at ${time}` : null;
};

export const formatMeetingDate = epochTimestamp => {
   if (!epochTimestamp) return null;
   const dt = DateTime.fromMillis(normalizeTimestamp(epochTimestamp));
   return dt.toFormat("ccc, dd MMM 'at' h:mm a");
};
